// Maison Jollie: background video behaviour for snippets/mj-media.liquid.
// - Loads the file only when the video comes near the screen, plays only while in view.
// - Never loads on data saver; no autoplay when the visitor prefers reduced motion.
// - Only one video plays at a time on the page.
// - Pause buttons: [data-mj-video-toggle] toggles the videos inside the closest [data-mj-video-scope].
// - Slideshows mark videos of hidden slides with the `inactive` attribute.

const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class MjVideo extends HTMLElement {
  static observedAttributes = ['inactive'];

  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video || saveData()) return;
    this.userPaused = reducedMotion();
    this.near = false;
    this.visible = false;

    this.nearObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.near = true;
        this.nearObserver.disconnect();
        this.update();
      }
    }, { rootMargin: '400px 0px' });
    this.nearObserver.observe(this);

    this.viewObserver = new IntersectionObserver((entries) => {
      this.visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35);
      this.update();
    }, { threshold: [0, 0.35, 0.6] });
    this.viewObserver.observe(this);

    this.video.addEventListener('playing', this.onPlaying);
    this.video.addEventListener('pause', this.onPause);
    document.addEventListener('mj:video-play', this.onOtherPlay);
    this.syncToggles();
  }

  disconnectedCallback() {
    this.nearObserver?.disconnect();
    this.viewObserver?.disconnect();
    document.removeEventListener('mj:video-play', this.onOtherPlay);
  }

  attributeChangedCallback() {
    this.update();
  }

  get loaded() {
    return this.video.querySelector('source[src]') !== null;
  }

  load() {
    if (this.loaded) return;
    this.video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    this.video.load();
  }

  update() {
    if (!this.video || saveData()) return;
    if (this.near) this.load();
    const shouldPlay = this.visible && !this.hasAttribute('inactive') && !this.userPaused && this.offsetParent !== null;
    if (shouldPlay) {
      this.load();
      this.video.play().catch(() => {});
    } else if (!this.video.paused) {
      this.video.pause();
    }
  }

  setPaused(paused) {
    this.userPaused = paused;
    this.update();
    this.syncToggles();
  }

  syncToggles() {
    const scope = this.closest('[data-mj-video-scope]');
    scope?.querySelectorAll('[data-mj-video-toggle]').forEach((button) => {
      button.hidden = saveData();
      button.setAttribute('aria-pressed', String(Boolean(this.userPaused)));
      button.setAttribute('aria-label', this.userPaused ? button.dataset.labelPlay : button.dataset.labelPause);
    });
  }

  onPlaying = () => {
    this.classList.add('is-playing');
    document.dispatchEvent(new CustomEvent('mj:video-play', { detail: this }));
  };

  onPause = () => {
    // keep the last frame visible; the fade only happens the first time
  };

  onOtherPlay = (event) => {
    if (event.detail !== this && !this.video.paused) this.video.pause();
  };
}

if (!customElements.get('mj-video')) customElements.define('mj-video', MjVideo);

// Pause / play buttons (not used by slideshows, which handle their own button)
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-mj-video-toggle]');
  if (!button) return;
  const scope = button.closest('[data-mj-video-scope]');
  const paused = button.getAttribute('aria-pressed') !== 'true';
  scope?.querySelectorAll('mj-video').forEach((video) => video.setPaused?.(paused));
});
